import { useQuery } from '@tanstack/react-query'
import {
	BarChart,
	CloseIcon,
	FiltersIcon,
	ModuleHeader,
	PieChart,
	Select,
	SpinnerWithBackground,
	Tags,
	Text,
} from '@viapeople/components'
import { useState } from 'react'
import { trpc } from '../../utils/trpc'
import { getCycleStepMapping, getTaskStatusMapping } from './utils'

export const ReportsOverview = () => {
	const [showFilters, setShowFilters] = useState(false)
	const [selectedCycles, setSelectedCycles] = useState<string[]>([])
	const [selectedCustomFields, setSelectedCustomFields] = useState<Record<string, string[]>>({})

	const cleanCustomFields = Object.fromEntries(
		Object.entries(selectedCustomFields).filter(([_, values]) => values.length > 0),
	)

	const { data, isLoading, isError } = useQuery(
		trpc.client.reportsStatusOverview.getDashboardCharts.queryOptions({
			cycleIds: selectedCycles,
			customFields: cleanCustomFields,
		}),
	)

	const handleCustomFieldChange = (fieldName: string, values: string[]) => {
		setSelectedCustomFields((prev) => {
			if (values.length === 0) {
				const { [fieldName]: _, ...rest } = prev
				return rest
			}
			return { ...prev, [fieldName]: values }
		})
	}

	const getSelectedTags = () => {
		const tags: { id: string; label: string }[] = []

		data?.cycles?.forEach((cycle) => {
			if (selectedCycles.includes(cycle.value)) {
				tags.push({
					id: `cycle-${cycle.value}`,
					label: cycle.label,
				})
			}
		})

		Object.entries(selectedCustomFields).forEach(([fieldName, values]) => {
			const fieldData = data?.participantsCustomFields?.[fieldName]
			const displayName = fieldData?.label || fieldName
			values.forEach((value) => {
				const valueDisplay = fieldData?.valueLabels?.[value] ?? value
				tags.push({
					id: `${fieldName}-${value}`,
					label: `${displayName}: ${valueDisplay}`,
				})
			})
		})

		return tags
	}

	const removeTag = (tagId: string) => {
		if (tagId.startsWith('cycle-')) {
			const cycleValue = tagId.replace('cycle-', '')
			setSelectedCycles((prev) => prev.filter((id) => id !== cycleValue))
		} else {
			const [fieldName, ...valueParts] = tagId.split('-')
			const value = valueParts.join('-')
			setSelectedCustomFields((prev) => {
				const currentValues = prev[fieldName] || []
				const newValues = currentValues.filter((val) => val !== value)

				if (newValues.length === 0) {
					const { [fieldName]: _, ...rest } = prev
					return rest
				}

				return {
					...prev,
					[fieldName]: newValues,
				}
			})
		}
	}

	const barChartData =
		data?.barChart?.map((item) => {
			const mapping = getCycleStepMapping(item.label)
			return {
				...mapping,
				value: item.value,
			}
		}) || []

	const employeePieChartData =
		data?.employeePieChart?.map((item) => {
			const mapping = getTaskStatusMapping(item.label)
			return {
				...mapping,
				value: item.value,
			}
		}) || []

	const managerPieChartData =
		data?.managerPieChart?.map((item) => {
			const mapping = getTaskStatusMapping(item.label)
			return {
				...mapping,
				value: item.value,
			}
		}) || []

	if (isLoading) {
		return <SpinnerWithBackground />
	}

	if (isError) {
		return (
			<div className='p-6 w-full'>
				<Text variant='small' className='text-red-500'>
					Failed to load the Status Overview report. Please refresh the page and try again.
				</Text>
			</div>
		)
	}

	return (
		<div data-testid='status-overview-page' className='p-6 w-full '>
			<div className='relative'>
				<ModuleHeader
					title='Status Overview'
					breadcrumb={[
						{ label: 'Dashboard', to: '/dashboard' },
						{ label: 'Reports & Files', to: '/reports' },
						{ label: 'Cycle Name' },
					]}
					buttons={[
						{
							label: 'Filters',
							variant: 'secondary',
							icon: FiltersIcon,
							onClick: () => setShowFilters(!showFilters),
						},
					]}
				/>

				{showFilters && (
					<div className='absolute min-w-40 max-w-[80%] bottom-[-10px] right-0 translate-y-1/1 bg-neutral-0 rounded-lg border border-neutral-200 z-10'>
						<div className='flex items-center p-4 justify-between border-b border-neutral-200'>
							<Text variant='h6' weight='semibold'>
								Filters
							</Text>
							<button type='button' className='cursor-pointer' onClick={() => setShowFilters(false)}>
								<CloseIcon />
							</button>
						</div>
						<div className='flex gap-4 p-4 flex-wrap max-h-96 overflow-y-auto'>
							{data?.cycles && data.cycles.length > 0 && (
								<div className='flex flex-col min-w-48'>
									<Text variant='small' weight='semibold' className='mb-3'>
										Cycles
									</Text>
									<Select
										isMulti
										placeholder='Select Cycles...'
										options={data.cycles.map((c) => ({ label: c.label, value: c.value }))}
										value={selectedCycles}
										onChange={(values: string[]) => setSelectedCycles(values)}
									/>
								</div>
							)}

							{data?.participantsCustomFields &&
								Object.entries(data.participantsCustomFields).map(([fieldKey, fieldData]) => (
									<div key={fieldKey} className='flex flex-col min-w-48'>
										<Text variant='small' weight='semibold' className='mb-3'>
											{fieldData.label}
										</Text>
										<Select
											isMulti
											placeholder={`Select ${fieldData.label}...`}
											options={fieldData.values.map((v: string) => ({
												label: fieldData.valueLabels?.[v] ?? v,
												value: v,
											}))}
											value={selectedCustomFields[fieldKey] || []}
											onChange={(values: string[]) => handleCustomFieldChange(fieldKey, values)}
										/>
									</div>
								))}
						</div>
					</div>
				)}
			</div>
			<div className='flex gap-2'>
				<Tags onRemove={removeTag} tags={getSelectedTags()} />
			</div>
			<div
				data-testid='status-overview-overall-section'
				className='flex flex-col gap-6 mt-6 p-6 rounded-lg border border-neutral-200'>
				<Text variant='h4' weight='semibold'>
					Overall
				</Text>
				<BarChart title='Overall Status' data={barChartData} showTooltips={false} />
			</div>
			<div
				data-testid='status-overview-distribution-section'
				className='flex flex-col gap-6 mt-6 p-6 rounded-lg border border-neutral-200'>
				<Text variant='h4' weight='semibold'>
					Status Distribution
				</Text>
				<div className='flex gap-4 justify-between'>
					{employeePieChartData.length > 0 && <PieChart title='Employee Status' data={employeePieChartData} />}
					{managerPieChartData.length > 0 && <PieChart title='Manager Status' data={managerPieChartData} />}
				</div>
			</div>
		</div>
	)
}
